/**
 * Servicio de Planes para Web Comercial
 * Gestión de planes de suscripción con optimización para frontend
 */

import { supabase } from '../lib/supabase';
import { CACHE_CONFIG, ERROR_MESSAGES } from '../constants/subscriptions';

/**
 * Clase para gestionar planes de suscripción
 */
class PlanService {
  constructor() {
    this.cache = new Map();
    this.cacheTimeouts = new Map();
  }

  /**
   * Obtiene todos los planes activos
   * @param {boolean} useCache - Usar caché local
   */
  async getPlans(useCache = true) {
    try {
      // Verificar caché primero
      if (useCache) {
        const cached = this.getCachedData('plans');
        if (cached) {
          return { success: true, plans: cached };
        }
      }

      const { data, error } = await supabase
        .from('planes')
        .select(`
          *,
          plan_features (
            id,
            feature_id,
            features (
              id,
              feature_name,
              feature_desc,
              feature_active
            )
          )
        `)
        .eq('es_activo', true)
        .order('precio', { ascending: true });

      if (error) throw error;

      // Guardar en caché
      if (useCache) {
        this.setCachedData('plans', data, CACHE_CONFIG.TTL.PLANS);
      }

      return { success: true, plans: data || [] };

    } catch (error) {
      console.error('Error en getPlans:', error);
      return { 
        success: false, 
        error: error.message || ERROR_MESSAGES.GENERIC_ERROR 
      };
    }
  }

  /**
   * Obtiene un plan específico por ID
   * @param {string} planId - ID del plan
   * @param {boolean} useCache - Usar caché local
   */
  async getPlanById(planId, useCache = true) {
    try {
      // Verificar caché primero
      const cacheKey = `plan_${planId}`;
      if (useCache) {
        const cached = this.getCachedData(cacheKey);
        if (cached) {
          return { success: true, plan: cached };
        }
      }

      const { data, error } = await supabase
        .from('planes')
        .select(`
          *,
          plan_features (
            id,
            feature_id,
            features (
              id,
              feature_name,
              feature_desc,
              feature_active
            )
          )
        `)
        .eq('es_activo', true)
        .eq('id', planId)
        .single();

      if (error) throw error;

      // Guardar en caché
      if (useCache) {
        this.setCachedData(cacheKey, data, CACHE_CONFIG.TTL.PLANS);
      }

      return { success: true, plan: data };

    } catch (error) {
      console.error('Error en getPlanById:', error);
      return { 
        success: false, 
        error: error.message || ERROR_MESSAGES.GENERIC_ERROR 
      };
    }
  }

  /**
   * Obtiene planes por tipo
   * @param {string} type - Tipo de plan
   */
  async getPlansByType(type) {
    try {
      const { data, error } = await supabase
        .from('planes')
        .select(`
          *,
          plan_features (
            id,
            feature_id,
            features (
              id,
              feature_name,
              feature_desc,
              feature_active
            )
          )
        `)
        .eq('es_activo', true)
        .eq('tipo', type)
        .order('precio', { ascending: true });

      if (error) throw error;

      return { success: true, plans: data || [] };

    } catch (error) {
      console.error('Error en getPlansByType:', error);
      return { 
        success: false, 
        error: error.message || ERROR_MESSAGES.GENERIC_ERROR 
      };
    }
  }

  /**
   * Compara planes para mostrar diferencias
   * @param {Array} plans - Array de planes a comparar
   */
  comparePlans(plans) {
    if (!plans || plans.length < 2) {
      return { success: false, error: 'Se necesitan al menos 2 planes para comparar' };
    }

    try {
      // Obtener todas las características únicas
      const allFeatures = new Set();
      plans.forEach(plan => {
        if (plan.plan_features) {
          plan.plan_features.forEach(feature => {
            if (feature.features) {
              allFeatures.add(feature.features.feature_name);
            }
          });
        }
      });

      // Crear matriz de comparación
      const comparison = Array.from(allFeatures).map(featureName => {
        const featureComparison = {
          name: featureName,
          plans: {}
        };

        plans.forEach(plan => {
          const planFeature = plan.plan_features?.find(f => f.features?.feature_name === featureName);
          featureComparison.plans[plan.id] = {
            included: planFeature ? planFeature.features?.feature_active : false,
            description: planFeature ? planFeature.features?.feature_desc : '',
            planId: plan.id
          };
        });

        return featureComparison;
      });

      return { success: true, comparison };

    } catch (error) {
      console.error('Error en comparePlans:', error);
      return { 
        success: false, 
        error: error.message || ERROR_MESSAGES.GENERIC_ERROR 
      };
    }
  }

  /**
   * Obtiene el plan más popular (basado en número de suscripciones)
   */
  async getPopularPlan() {
    try {
      const { data, error } = await supabase
        .from('vista_popularidad_planes')
        .select('*')
        .eq('activo', true)
        .order('cantidad_suscripciones', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (!data) {
        // Si no hay datos de popularidad, obtener el plan más caro como "popular"
        const { data: plans, error: plansError } = await supabase
          .from('planes')
          .select('*')
          .eq('es_activo', true)
          .eq('es_destacado', true)
          .single();

        if (plansError) {
          // Si no hay plan marcado como popular, obtener el más caro
          const { data: allPlans, error: allPlansError } = await supabase
            .from('planes')
            .select('*')
            .eq('es_activo', true)
            .order('plan_price', { ascending: false })
            .limit(1)
            .single();

          if (allPlansError) throw allPlansError;
          return { success: true, plan: allPlans };
        }

        return { success: true, plan: plans };
      }

      return { success: true, plan: data };

    } catch (error) {
      console.error('Error en getPopularPlan:', error);
      return { 
        success: false, 
        error: error.message || ERROR_MESSAGES.GENERIC_ERROR 
      };
    }
  }

  /**
   * Obtiene estadísticas de planes
   */
  async getPlanStats() {
    try {
      const { data, error } = await supabase
        .from('vista_estadisticas_planes')
        .select('*')
        .eq('activo', true)
        .order('cantidad_suscripciones', { ascending: false });

      if (error) throw error;

      return { success: true, stats: data || [] };

    } catch (error) {
      console.error('Error en getPlanStats:', error);
      return { 
        success: false, 
        error: error.message || ERROR_MESSAGES.GENERIC_ERROR 
      };
    }
  }

  /**
   * Valida si un usuario puede cambiar a un plan específico
   * @param {string} userId - ID del usuario
   * @param {string} targetPlanId - ID del plan destino
   */
  async validatePlanChange(userId, targetPlanId) {
    try {
      // Obtener suscripción actual del usuario
      const { data: currentSubscription, error: subError } = await supabase
        .from('suscripciones')
        .select('*, planes (*)')
        .eq('usuario_id', userId)
        .eq('estado', 'authorized')
        .single();

      if (subError && subError.code !== 'PGRST116') {
        throw subError;
      }

      // Obtener plan destino
      const { data: targetPlan, error: planError } = await supabase
        .from('planes')
        .select('*')
        .eq('id', targetPlanId)
        .eq('es_activo', true)
        .single();

      if (planError) throw planError;

      // Validaciones
      const validation = {
        canChange: true,
        reason: '',
        requirements: []
      };

      // Si no tiene suscripción actual, puede contratar cualquier plan
      if (!currentSubscription) {
        validation.reason = 'Puedes contratar este plan';
        return { success: true, validation };
      }

      // No puede cambiar al mismo plan
      if (currentSubscription.plan_id === targetPlanId) {
        validation.canChange = false;
        validation.reason = 'Ya tienes este plan';
        return { success: true, validation };
      }

      // Validar si es un upgrade o downgrade
      const currentPrice = currentSubscription.planes.plan_price;
      const targetPrice = targetPlan.plan_price;

      if (targetPrice > currentPrice) {
        validation.reason = 'Upgrade de plan - Se aplicará prorrateo';
        validation.requirements.push('Pago de diferencia prorrateada');
      } else if (targetPrice < currentPrice) {
        validation.reason = 'Downgrade de plan - Cambio efectivo próximo ciclo';
        validation.requirements.push('El cambio se aplicará en el próximo ciclo de facturación');
      } else {
        validation.reason = 'Cambio a plan con mismo precio';
      }

      return { success: true, validation };

    } catch (error) {
      console.error('Error en validatePlanChange:', error);
      return { 
        success: false, 
        error: error.message || ERROR_MESSAGES.GENERIC_ERROR 
      };
    }
  }

  /**
   * Busca planes por texto
   * @param {string} searchText - Texto a buscar
   */
  async searchPlans(searchText) {
    try {
      const { data, error } = await supabase
        .from('planes')
        .select(`
          *,
          plan_features (
            id,
            feature_id,
            features (
              id,
              feature_name,
              feature_desc,
              feature_active
            )
          )
        `)
        .eq('es_activo', true)
        .or(`nombre.ilike.%${searchText}%,descripcion.ilike.%${searchText}%`)
        .order('plan_price', { ascending: true });

      if (error) throw error;

      return { success: true, plans: data || [] };

    } catch (error) {
      console.error('Error en searchPlans:', error);
      return { 
        success: false, 
        error: error.message || ERROR_MESSAGES.GENERIC_ERROR 
      };
    }
  }

  /**
   * Obtiene planes recomendados para un usuario
   * @param {string} userId - ID del usuario (opcional)
   */
  async getRecommendedPlans(userId = null) {
    try {
      let plans = [];

      if (userId) {
        // Si hay usuario, obtener recomendaciones basadas en su comportamiento
        const { data, error } = await supabase
          .from('vista_recomendaciones_planes')
          .select('*')
          .eq('usuario_id', userId)
          .eq('activo', true)
          .order('score', { ascending: false })
          .limit(3);

        if (error && error.code !== 'PGRST116') {
          throw error;
        }

        if (data && data.length > 0) {
          plans = data.map(item => item.plan);
        }
      }

      // Si no hay recomendaciones personalizadas, obtener planes populares
      if (plans.length === 0) {
        const { data: popularPlans, error: popularError } = await supabase
          .from('planes')
          .select('*')
          .eq('es_activo', true)
          .eq('plan_status', 1)
          .order('plan_price', { ascending: true })
          .limit(3);

        if (popularError) throw popularError;
        plans = popularPlans || [];
      }

      // Si todavía no hay planes, obtener los primeros 3 activos
      if (plans.length === 0) {
        const { data: defaultPlans, error: defaultError } = await supabase
          .from('planes')
          .select('*')
          .eq('es_activo', true)
          .order('plan_price', { ascending: true })
          .limit(3);

        if (defaultError) throw defaultError;
        plans = defaultPlans || [];
      }

      return { success: true, plans };

    } catch (error) {
      console.error('Error en getRecommendedPlans:', error);
      return { 
        success: false, 
        error: error.message || ERROR_MESSAGES.GENERIC_ERROR 
      };
    }
  }

  /**
   * Métodos de caché
   */
  setCachedData(key, data, ttl) {
    this.cache.set(key, data);
    this.cacheTimeouts.set(key, Date.now() + ttl);
  }

  getCachedData(key) {
    const timeout = this.cacheTimeouts.get(key);
    if (timeout && Date.now() > timeout) {
      this.cache.delete(key);
      this.cacheTimeouts.delete(key);
      return null;
    }
    return this.cache.get(key) || null;
  }

  clearCache(key) {
    if (key) {
      this.cache.delete(key);
      this.cacheTimeouts.delete(key);
    } else {
      this.cache.clear();
      this.cacheTimeouts.clear();
    }
  }
}

// Exportar instancia única
const planService = new PlanService();

export default planService;
